import React, { useState } from 'react';

const RitualDetailsModal = ({ 
  isOpen, 
  onClose, 
  ritual,
  onToggleFavorite,
  onDelete,
  onUpdateNotes
}) => {
  const [notes, setNotes] = useState(ritual?.notes || '');
  const [isEditingNotes, setIsEditingNotes] = useState(false);

  if (!isOpen || !ritual) return null;

  const getEmojiForCulture = (culture) => {
    const emojiMap = {
      'western': '💍',
      'indian': '🔥',
      'chinese': '🍵',
      'jewish': '✡️',
      'african': '🥁',
      'islamic': '☪️',
      'japanese': '🍶',
      'korean': '🎎',
      'latin_american': '💃',
      'celtic': '☘️',
      'southeast_asian': '🌺',
      'modern': '✨'
    };
    return emojiMap[culture] || '🎊';
  };

  const handleSaveNotes = () => {
    onUpdateNotes(notes);
    setIsEditingNotes(false);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/40 backdrop-blur-lg z-50 flex items-center justify-center p-4 animate-in fade-in duration-500"
      onClick={onClose}
    >
      <div className="bg-gradient-to-br from-white/95 via-rose-50/30 to-champagne-50/30 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto transform animate-in zoom-in slide-in-from-bottom-4 duration-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Floating VowAI Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
          <div className="absolute top-4 left-8 text-white/30 text-xl animate-pulse">✨</div>
          <div className="absolute top-8 right-16 text-white/40 text-lg animate-pulse" style={{animationDelay: '0.5s'}}>💫</div>
          <div className="absolute bottom-6 left-16 text-white/30 text-sm animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
          <div className="absolute top-12 right-8 text-rose-300/20 text-2xl animate-bounce" style={{animationDelay: '0.3s', animationDuration: '3s'}}>💖</div>
          <div className="absolute bottom-12 left-8 text-pink-300/25 text-xl animate-bounce" style={{animationDelay: '1.5s', animationDuration: '4s'}}>💕</div>
          <div className="absolute top-16 right-20 w-16 h-16 bg-gradient-to-br from-rose-200/10 to-pink-300/15 rounded-full blur-xl animate-pulse" style={{animationDelay: '0s', animationDuration: '4s'}}></div>
          <div className="absolute bottom-20 left-12 w-20 h-20 bg-gradient-to-br from-champagne-200/10 to-gold-300/15 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s', animationDuration: '5s'}}></div>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-gold-500 p-8 rounded-t-3xl relative overflow-hidden">
          {/* Header magical elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
            <div className="absolute top-4 left-8 text-white/20 text-2xl animate-pulse">✨</div>
            <div className="absolute top-8 right-16 text-white/30 text-lg animate-pulse" style={{animationDelay: '0.5s'}}>💫</div>
            <div className="absolute bottom-6 left-16 text-white/20 text-sm animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
          </div>

          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:rotate-90 transition-all duration-300 z-10"
          >
            <span className="text-xl">✕</span>
          </button>
          
          <div className="flex items-center space-x-6 relative z-10">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm border border-white/30">
              <span className="text-5xl">{getEmojiForCulture(ritual.culture)}</span>
            </div>
            <div>
              <h2 className="text-3xl font-serif font-bold text-white mb-2 drop-shadow-lg">
                {ritual.name}
              </h2>
              <p className="text-white/90 italic text-lg drop-shadow-md">
                {ritual.culture_display_name}
              </p>
              <div className="flex items-center space-x-3 mt-3 flex-wrap gap-2">
                {ritual.duration_minutes && (
                  <span className="px-3 py-1 bg-white/60 rounded-full text-sm font-bold text-gray-700">
                    ⏱️ {ritual.duration_minutes} min
                  </span>
                )}
                {ritual.complexity && (
                  <span className="px-3 py-1 bg-white/60 rounded-full text-sm font-bold text-gray-700 capitalize">
                    📊 {ritual.complexity}
                  </span>
                )}
                {ritual.formality && (
                  <span className="px-3 py-1 bg-white/60 rounded-full text-sm font-bold text-gray-700 capitalize">
                    👔 {ritual.formality}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6 relative">
          
          {/* Floating content elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-10 right-8 text-rose-200/20 text-xl animate-bounce" style={{animationDelay: '0.8s', animationDuration: '4s'}}>🎭</div>
            <div className="absolute bottom-20 left-6 text-champagne-300/25 text-lg animate-bounce" style={{animationDelay: '2s', animationDuration: '3.5s'}}>🌸</div>
          </div>

          {/* Origin */}
          <div className="relative">
            <div className="absolute -top-2 -left-2 w-full h-full bg-gradient-to-br from-rose-100/20 to-pink-100/20 rounded-xl blur-sm"></div>
            <div className="relative bg-gradient-to-br from-white/80 to-rose-50/40 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 group">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="text-2xl mr-2 group-hover:animate-bounce">📜</span>
                Origin
              </h3>
              <p className="text-gray-700 leading-relaxed">{ritual.origin}</p>
            </div>
          </div>

          {/* Description */}
          <div className="relative">
            <div className="absolute -top-2 -left-2 w-full h-full bg-gradient-to-br from-purple-100/20 to-pink-100/20 rounded-xl blur-sm"></div>
            <div className="relative bg-gradient-to-br from-white/80 to-purple-50/40 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 group">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="text-2xl mr-2 group-hover:animate-bounce">📖</span>
                Description
              </h3>
              <p className="text-gray-700 leading-relaxed">{ritual.description}</p>
            </div>
          </div>

          {/* Meaning */}
          <div className="relative">
            <div className="absolute -top-2 -left-2 w-full h-full bg-gradient-to-br from-gold-100/20 to-yellow-100/20 rounded-xl blur-sm"></div>
            <div className="relative bg-gradient-to-br from-white/80 to-gold-50/40 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 group">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="text-2xl mr-2 group-hover:animate-bounce">💫</span>
                Meaning & Significance
              </h3>
              <p className="text-gray-700 leading-relaxed">{ritual.meaning}</p>
            </div>
          </div>

          {/* Steps */}
          {ritual.steps && ritual.steps.length > 0 && (
            <div className="relative">
              <div className="absolute -top-2 -left-2 w-full h-full bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-xl blur-sm"></div>
              <div className="relative bg-gradient-to-br from-white/80 to-blue-50/40 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 group">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-2xl mr-2 group-hover:animate-bounce">📝</span>
                  Steps
                </h3>
                <ol className="list-decimal list-inside space-y-2">
                  {ritual.steps.map((step, index) => (
                    <li key={index} className="text-gray-700 leading-relaxed">{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Grid for Items and Participants */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Items Needed */}
            {ritual.items_needed && ritual.items_needed.length > 0 && (
              <div className="group bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-4 border border-rose-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl group-hover:animate-bounce">🎁</span>
                  <span className="font-bold text-gray-700">Items Needed</span>
                </div>
                <ul className="list-disc list-inside space-y-1">
                  {ritual.items_needed.map((item, index) => (
                    <li key={index} className="text-gray-700">{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Participants */}
            {ritual.participants && ritual.participants.length > 0 && (
              <div className="group bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl group-hover:animate-bounce">👥</span>
                  <span className="font-bold text-gray-700">Participants</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ritual.participants.map((participant, index) => (
                    <span key={index} className="px-3 py-1 bg-white/60 text-gray-700 text-sm rounded-full capitalize font-medium">
                      {participant}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Personal Notes */}
          <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-serif font-bold text-yellow-700 flex items-center">
                <span className="mr-2">📝</span>
                Your Notes
              </h3>
              {!isEditingNotes && (
                <button
                  onClick={() => setIsEditingNotes(true)}
                  className="px-4 py-2 bg-yellow-200 hover:bg-yellow-300 text-yellow-800 rounded-xl font-semibold text-sm transition-all duration-300"
                >
                  ✏️ Edit
                </button>
              )}
            </div>
            {isEditingNotes ? (
              <div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add your personal notes about this ritual..."
                  className="w-full p-4 border-2 border-yellow-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-400 transition-all duration-300 min-h-[100px]"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleSaveNotes}
                    className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold hover:shadow-lg transition-all duration-300"
                  >
                    💾 Save
                  </button>
                  <button
                    onClick={() => {
                      setNotes(ritual.notes || '');
                      setIsEditingNotes(false);
                    }}
                    className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-bold transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-700 italic">
                {ritual.notes || 'No notes yet. Click Edit to add your thoughts!'}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={onToggleFavorite}
              className="px-8 py-3 bg-gradient-to-r from-gold-400 to-yellow-400 hover:from-gold-500 hover:to-yellow-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              {ritual.is_favorite ? '⭐ Remove from Favorites' : '☆ Add to Favorites'}
            </button>
            <button
              onClick={onDelete}
              className="px-8 py-3 bg-gradient-to-r from-red-400 to-rose-400 hover:from-red-500 hover:to-rose-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              🗑️ Remove Ritual
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RitualDetailsModal;
